import React from 'react';
import { SelectFieldComponent } from '../../../../components/common/SelectFieldComponent';
import { EspecieService } from '../services/especieService';
import { type EspecieListItem } from '../types/especie';

export interface EspecieSelectProps {
  /** Current selected value */
  value?: number | string;
  /** Change handler */
  onChange: (value: string | number) => void;
  onSelect?: (value: string | number, label: string) => void;
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Custom filter function to filter especies */
  filter?: (especie: EspecieListItem) => boolean;
  /** Custom sx prop for styling */
  sx?: any;
}

export const EspecieSelect: React.FC<EspecieSelectProps> = ({
  value,
  onChange,
  onSelect,
  label = 'Especie',
  error,
  disabled = false,
  required = false,
  placeholder,
  filter,
  sx,
}) => {
  const [especies, setEspecies] = React.useState<EspecieListItem[]>([]);
  const [loading, setLoading] = React.useState(false);

  // Fetch especies on component mount
  const fetchEspecies = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await EspecieService.getEspecies();
      setEspecies(data);
    } catch (error) {
      console.error('Error fetching especies:', error);
      setEspecies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchEspecies();
  }, [fetchEspecies]);

  // Filter especies based on props
  const filteredEspecies = React.useMemo(() => {
    let filtered = especies;

    // Apply custom filter if provided
    if (filter) {
      filtered = filtered.filter(filter);
    }

    return filtered;
  }, [especies, filter]);

  // Convert especies to select options
  const options = React.useMemo(() => {
    return filteredEspecies.map((especie) => ({
      value: especie.idEspecie,
      label: especie.nombreComun,
    }));
  }, [filteredEspecies]);

  // Handle error state
  const displayError = error;

  return (
    <SelectFieldComponent
      label={required ? `${label} *` : label}
      value={value || ''}
      onChange={onChange}
      onSelect={onSelect}
      options={options}
      error={displayError}
      disabled={disabled || loading}
      placeholder={placeholder}
      loading={loading}
      sx={sx}
    />
  );
};

export default EspecieSelect;